import { apiClient } from '@/api/client';
import {
  NoteWire,
  noteWireSchema,
  NoteCreate,
  NoteUpdate,
  NotePhotoWire,
  notePhotoWireSchema,
  PaginatedNoteResponseWire,
  paginatedNoteResponseSchema,
} from '../schemas/note';

/**
 * Domain-oriented API module for note operations
 * Provides typed functions for note CRUD from the Gateway API
 */

/**
 * Query parameters for listing notes
 */
export interface ListNotesParams {
  business_id?: string;
  menu_item_id?: string;
  page?: number;
  per_page?: number;
  sort_order?: 'asc' | 'desc';
}

/**
 * Build query string from parameters
 */
function buildQueryString(params: ListNotesParams): string {
  const queryParams: string[] = [];

  if (params.business_id) {
    queryParams.push(`business_id=${encodeURIComponent(params.business_id)}`);
  }
  if (params.menu_item_id) {
    queryParams.push(`menu_item_id=${encodeURIComponent(params.menu_item_id)}`);
  }
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }
  if (params.sort_order) {
    queryParams.push(`sort_order=${params.sort_order}`);
  }

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

/**
 * Create a new note
 *
 * @param data - Note creation data
 * @returns Created note
 *
 * @example
 * ```typescript
 * const note = await createNote({
 *   business_id: 'business-123',
 *   menu_item_id: 'menu-item-456',
 *   content: 'Great flavor, slightly spicy'
 * });
 * ```
 */
export async function createNote(data: NoteCreate): Promise<NoteWire> {
  const endpoint = '/api/notes';
  const wireData = await apiClient.post<NoteWire>(endpoint, data);

  // Validate response with zod schema
  return noteWireSchema.parse(wireData);
}

/**
 * Get the current user's notes with optional filtering
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of notes
 *
 * @example
 * ```typescript
 * const result = await getNotes({
 *   business_id: 'business-123',
 *   page: 1,
 *   per_page: 20
 * });
 * ```
 */
export async function getNotes(
  params: ListNotesParams = {}
): Promise<PaginatedNoteResponseWire> {
  const queryParams: ListNotesParams = {
    page: 1,
    per_page: 20,
    sort_order: 'desc',
    ...params,
  };

  const queryString = buildQueryString(queryParams);
  const endpoint = `/api/notes${queryString}`;

  const wireData = await apiClient.get<PaginatedNoteResponseWire>(endpoint);

  // Validate response with zod schema
  return paginatedNoteResponseSchema.parse(wireData);
}

/**
 * Get a single note by ID
 *
 * @param id - Note ID
 * @returns Note
 *
 * @example
 * ```typescript
 * const note = await getNoteById('note-123');
 * ```
 */
export async function getNoteById(id: string): Promise<NoteWire> {
  const endpoint = `/api/notes/${id}`;
  const wireData = await apiClient.get<NoteWire>(endpoint);

  // Validate response with zod schema
  return noteWireSchema.parse(wireData);
}

/**
 * Update an existing note's content
 *
 * @param id - Note ID
 * @param data - Note update data
 * @returns Updated note
 *
 * @example
 * ```typescript
 * const note = await updateNote('note-123', {
 *   content: 'Updated tasting notes'
 * });
 * ```
 */
export async function updateNote(
  id: string,
  data: NoteUpdate
): Promise<NoteWire> {
  const endpoint = `/api/notes/${id}`;
  const wireData = await apiClient.put<NoteWire>(endpoint, data);

  // Validate response with zod schema
  return noteWireSchema.parse(wireData);
}

/**
 * Delete a note (hard delete)
 *
 * @param id - Note ID
 *
 * @example
 * ```typescript
 * await deleteNote('note-123');
 * ```
 */
export async function deleteNote(id: string): Promise<void> {
  const endpoint = `/api/notes/${id}`;
  await apiClient.delete(endpoint);
}

/**
 * Upload a photo for a note (max 1 per note)
 *
 * @param noteId - Note ID
 * @param file - File to upload
 * @returns Uploaded photo metadata
 *
 * @example
 * ```typescript
 * const photo = await uploadNotePhoto('note-123', {
 *   uri: 'file:///path/to/photo.jpg',
 *   type: 'image/jpeg',
 *   name: 'photo.jpg'
 * });
 * ```
 */
export async function uploadNotePhoto(
  noteId: string,
  file: { uri: string; type?: string; name?: string; file?: File }
): Promise<NotePhotoWire> {
  const endpoint = `/api/notes/${noteId}/photo`;
  const wireData = await apiClient.uploadFile<NotePhotoWire>(
    endpoint,
    file,
    'photo'
  );

  // Validate response with zod schema
  return notePhotoWireSchema.parse(wireData);
}

/**
 * Delete a note's photo
 *
 * @param noteId - Note ID
 *
 * @example
 * ```typescript
 * await deleteNotePhoto('note-123');
 * ```
 */
export async function deleteNotePhoto(noteId: string): Promise<void> {
  const endpoint = `/api/notes/${noteId}/photo`;
  await apiClient.delete(endpoint);
}
